import { matchPath } from 'react-router';
import { resolveStorefront } from 'src/pages/storefrontPage/utils';
import { defaultRumClient } from 'src/common/metrics';
import { appendRequestQueryParams } from 'src/utils/links';
import { parseAsins } from 'src/utils/asinListUtils';
import { getMarketplace, MarketplaceToTopLevelDomain } from 'src/utils/marketplace';
import { sendOpenLinkMessage } from 'src/utils/messages';
import { routes } from 'src/utils/routes';
import { getMobileOperatingSystem, getSma, Platform, Sma } from 'src/utils/user';
/**
 * ACI namespace to the mShop content path segment it is served under.
 *
 * mShop serves creator content at `/finds/post/<type>/<aci>`, and the type is carried by the ACI's
 * own namespace — the same thing the backend reads via `ContentIdentifierMapping.lookup` in
 * `AciUtil.parseAndValidateAci`, and the same construction `AMPOndemandPythonScript` uses. So the
 * destination needs no API call. These are the three types Painter supports; an ACI outside them
 * would have been rejected by the backend anyway, so it falls through to the marketplace home.
 */
const ACI_PREFIX_TO_CONTENT_PATH = {
    'amzn1.vse.video.': 'video',
    'amzn1.shoppablemedia.v1.': 'photo',
    'amzn1.spark.': 'photo',
};
/**
 * Where on Amazon a fallback session should land, based on the Painter page it is leaving.
 *
 * Painter 1.0 preserved the whole pathname, so every page type kept its context. Mapping only the
 * PDP would drop storefront and creator sessions on the Amazon homepage, losing the ad they came
 * from, so each route resolves to its closest Amazon equivalent:
 *
 * - **PDP** — the ASIN is in the path, so `/dp/<asin>` is always available.
 * - **Storefront** — `storefrontMetadata.json` already carries a per-storefront `fallbackUrl`
 *   (`amazon.com/shop/<handle>`, `amazon.com/stores/page/<id>`) for exactly this purpose. Static,
 *   so it needs no API call. An unknown id resolves to `defaultStorefront`, whose fallbackUrl is
 *   the homepage.
 * - **Creator content** — the `aci` maps to the mShop content page at `/finds/post/<type>/<aci>`,
 *   so this is derived from the path too. Note the upstream `viewOnAmazon` link additionally
 *   carries a `creator_attr_token`, which is what credits the creator; a URL built here has no
 *   token, so the creator loses attribution on the visit even though the customer reaches the
 *   right page.
 * - **Checkout and link campaign** — no meaningful Amazon equivalent mid flow, so the homepage.
 *
 * Every destination is either built from `baseUrl` or read from `storefrontMetadata.json`, so all
 * of them are known good absolute URLs and none depends on an API call — which matters because
 * this runs precisely when the init call has already failed. Storefront destinations live on their
 * own host, which is why this returns a full URL rather than a path to append to `baseUrl`.
 */
const resolveAmazonDestination = (baseUrl) => {
    var _a, _b;
    const { pathname } = window.location;
    const pdpMatch = matchPath(routes.productDetailPage, pathname);
    if (pdpMatch === null || pdpMatch === void 0 ? void 0 : pdpMatch.params.asin) {
        return `${baseUrl}/dp/${pdpMatch.params.asin}`;
    }
    const storefrontMatch = matchPath(routes.storefrontPage, pathname);
    if (storefrontMatch) {
        // resolveStorefront falls back to defaultStorefront for an unknown id, so meta is always set
        return resolveStorefront(storefrontMatch.params.id).meta.fallbackUrl;
    }
    const aci = (_a = matchPath(routes.creatorStorefrontPage, pathname)) === null || _a === void 0 ? void 0 : _a.params.aci;
    if (aci) {
        const contentPath = (_b = Object.entries(ACI_PREFIX_TO_CONTENT_PATH).find(([prefix]) => aci.startsWith(prefix))) === null || _b === void 0 ? void 0 : _b[1];
        if (contentPath) {
            return `${baseUrl}/finds/post/${contentPath}/${aci}`;
        }
    }
    // Multi-ASIN featured page — no per-page storefrontMetadata fallbackUrl exists, so the closest
    // Amazon equivalent is the detail page for the first accepted ASIN from the `asins` param. An
    // absent/empty/all-invalid list leaves no ASIN, so it falls through to the marketplace home.
    // (Stopgap: linking to one ASIN loses the rest of the collection — tracked as Open Question 2.)
    if (matchPath(routes.multiAsinLandingPage, pathname)) {
        const [firstAsin] = parseAsins(new URLSearchParams(window.location.search).get('asins'));
        if (firstAsin) {
            return `${baseUrl}/dp/${firstAsin}`;
        }
    }
    return baseUrl;
};
// redirectToAmazonApp can be called more than once in a session — App.tsx / MissingAuthPage fire
// it on mount, and each interstitial "Go now" tap fires it again. The redirect_to_amazon_app event
// is the "this session fell back" signal, so it must count once per session, not per call, or the
// redirect-rate dashboard over-counts. Retries are tracked separately by
// REDIRECT_TO_AMAZON_GO_NOW_CLICKED. Module-level, so it resets on the next page load (= session).
let redirectEventRecorded = false;
export const redirectToAmazonApp = (reason, { preferDirectNavigation } = {}) => {
    const searchParams = new URLSearchParams(window.location.search);
    // getMarketplace validates the param, falls back to hostname detection, then to US, so
    // the TLD lookup always resolves and the URL is never malformed.
    const baseUrl = `https://www.amazon${MarketplaceToTopLevelDomain[getMarketplace(searchParams)]}`;
    // marketplaceId is a Painter internal param and the TLD above already encodes the
    // marketplace, so drop it instead of forwarding it onto a customer facing amazon URL.
    searchParams.delete('marketplaceId');
    searchParams.set('handshakeExit', reason);
    // Merges rather than replaces, since storefront and creator destinations can carry their own query
    const redirectUrl = appendRequestQueryParams(searchParams, resolveAmazonDestination(baseUrl));
    if (!redirectEventRecorded) {
        redirectEventRecorded = true;
        defaultRumClient.recordEvent('redirect_to_amazon_app', {
            reason,
            redirectUrl,
            currentUrl: window.location.href,
        });
    }
    // The customer is still here after the host was already asked once, so it is not acting on
    // OPEN_EXTERNAL_LINK. Give up the app handoff and navigate, in every context — a working web
    // PDP beats a page that never moves.
    if (preferDirectNavigation) {
        defaultRumClient.recordEvent('redirect_to_amazon_app_direct', { reason, redirectUrl });
        window.location.href = redirectUrl;
        return;
    }
    // Hand the link to the host SMA rather than assigning window.location. A programmatic
    // navigation inside an SMA webview would keep the customer in that webview on Amazon mobile
    // web, so the universal link would never reach the mShop app. sendOpenLinkMessage owns every
    // fallback for hosts that cannot take the message — the TikTok and Snapchat iOS workaround
    // and the no-bridge case — so there is nothing to branch on here. reportAction is
    // deliberately skipped, matching InvalidAuthPage: both fallback triggers mean there is no
    // working auth to report the transit with.
    sendOpenLinkMessage(defaultRumClient, redirectUrl);
};
/**
 * Utility function to handle redirecting from Painter 2.0 to Painter 1.0 (legacy).
 *
 * @deprecated No longer wired to any call site. Retained temporarily so an emergency
 * Apollo rollback restores the previous helper without a re-import. Delete in a
 * follow-up CR once the mShop launch bakes and Painter 1.0 traffic drains to zero.
 *
 * @param reason - The reason for the redirect
 */
export const redirectToLegacyPainter = (reason) => {
    const queryString = window.location.search;
    const searchParams = new URLSearchParams(queryString);
    // Determine marketplace based on marketplaceId parameter
    const isCanada = searchParams.get('marketplaceId') === 'CA' || window.location.hostname.includes('amazon.ca');
    // Snapchat Android has a bug where cookies aren't set correctly, maybe the samesite token?,
    // so we always redirect to shop.social.amazon for that platform
    const isSnapchatAndroid = getSma() === Sma.SNAPCHAT && getMobileOperatingSystem() === Platform.ANDROID;
    const baseUrl = reason === 'missing_auth' && !isSnapchatAndroid
        ? 'https://web.shop-external.amazon'
        : `https://shop.social.amazon.${isCanada ? 'ca' : 'com'}`;
    // Add fallback parameter and preserve all current query parameters
    searchParams.set('fallbackFromPainter20', 'true');
    searchParams.set('fallbackReason', reason);
    if (reason === 'missing_auth') {
        searchParams.set('dexp', 'missingAuthIn20');
    }
    const redirectUrl = `${baseUrl}${location.pathname}?${searchParams.toString()}`;
    // Emit RUM event before redirecting
    defaultRumClient.recordEvent('redirect_to_legacy_painter', {
        reason,
        redirectUrl,
        currentUrl: window.location.href,
    });
    window.location.href = redirectUrl;
};
